import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './columnized-content.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import {
    KontentStringValue,
    KontentMedia,
    KontentRichText,
    CTAConfiguration,
    BrandColor,
} from '../../../types/KontentProps';
import { RichTextEl } from '../Global/RichText';
import {
    getMediaData,
    getMultiChoice,
    getColor,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import ArrowLgButton from '../Global/ArrowLgButton';
import LazyImage from '../Global/LazyImage';
import { KontentRadioMultiChoice } from '../../../types/KontentProps';

type ContentPanel = {
    elements: CTAConfiguration & {
        headline: KontentStringValue;
        sub_title: KontentStringValue;
        image: KontentMedia;
        description: KontentRichText;
        content_alignment: KontentRadioMultiChoice<'left' | 'center' | 'right'>;
    };
};

interface Props {
    kontentItemId?: string;
    line_color: BrandColor;
    content_panels: { value: ContentPanel[] };
}

const ColumnizedContent: React.FC<Props> = ({
    kontentItemId,
    content_panels,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const currentDisplayNumber = breakpoints.l ? 3 : 1;
    const contentPanels = content_panels.value;
    const initVisiblePanels = contentPanels.slice(0, currentDisplayNumber);
    const [visiblePanels, setVisiblePanels] =
        useState<ContentPanel[]>(initVisiblePanels);
    const [currentPanel, setCurrentPanel] = useState(0);
    const numPanels = contentPanels.length;
    const strokeColor = '#505251';
    const lineColor = getColor(props.line_color);

    useEffect(() => {
        setVisiblePanels(initVisiblePanels);
        setCurrentPanel(0);
    }, [currentDisplayNumber]);

    const getPanelData = (panel: ContentPanel) => {
        let WindowActionStructure =
            panel.elements?.cta_configuration__cta_window_action;
        let ctaWindowAction = getMultiChoice(WindowActionStructure);

        if (ctaWindowAction == null && WindowActionStructure?.value[0]?.name) {
            ctaWindowAction = WindowActionStructure?.value[0]?.name
                ?.toLowerCase()
                ?.split(' ')
                ?.join('_');
        }

        return {
            headline: panel.elements?.headline?.value,
            sub_title: panel.elements?.sub_title.value,
            image: getMediaData(panel.elements?.image),
            description: panel.elements?.description,
            cta_label: panel.elements?.cta_configuration__cta_label?.value,
            cta_location: BuildLinkHref(
                panel.elements?.cta_configuration__cta_location
            ),
            cta_window_action: ctaWindowAction,
            cta_anchor_point:
                panel?.elements?.cta_configuration__cta_location_anchor_point
                    ?.value || undefined,
        };
    };

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newPanel = currentPanel - 1;
        setVisiblePanels(
            contentPanels?.slice(newPanel, newPanel + currentDisplayNumber)
        );
        setCurrentPanel(newPanel);
    };

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newPanel = currentPanel + 1;
        setVisiblePanels(
            contentPanels?.slice(newPanel, newPanel + currentDisplayNumber)
        );
        setCurrentPanel(newPanel);
    };

    const renderArrowPrev = () => {
        if (currentPanel > 0) {
            return (
                <ArrowLgButton
                    direction="left"
                    strokeColor={strokeColor}
                    className={styles.prev}
                    handleClick={handleClickPrev}
                />
            );
        }
    };

    const renderArrowNext = () => {
        if (currentPanel < numPanels - currentDisplayNumber) {
            return (
                <ArrowLgButton
                    direction="right"
                    strokeColor={strokeColor}
                    className={styles.next}
                    handleClick={handleClickNext}
                />
            );
        }
    };

    const renderPanel = (panel: ContentPanel, idx: number) => {
        if (breakpoints.l || idx === 0) {
            const panelData = getPanelData(panel);
            const ctaTarget =
                panelData.cta_window_action === 'new_window'
                    ? '_blank'
                    : '_self';
            const authorStyles = { borderColor: lineColor || undefined };
            const descriptionAlignment = getMultiChoice(
                panel.elements.content_alignment
            );
            const descriptionStyle = descriptionAlignment
                ? { textAlign: descriptionAlignment }
                : { textAlign: 'center' };

            return (
                <div
                    className={styles.panel}
                    key={idx}
                    style={authorStyles}
                    id={panelData.cta_anchor_point}
                >
                    <div className={styles.panelTitle}>
                        {!breakpoints.l && renderArrowNext()}

                        {!breakpoints.l && renderArrowPrev()}
                        {panelData.image?.url && (
                            <div data-kontent-element-codename="image">
                                <LazyImage
                                    {...getDisplayDimensions(
                                        panelData.image.width,
                                        panelData.image.height,
                                        imagePreset.card.desktop.w
                                    )}
                                    src={optimizeKontentImageUrl(
                                        panelData.image.url,
                                        {
                                            ...imagePreset.card.desktop,
                                            q: 95,
                                        }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        panelData.image.url,
                                        imagePreset.card.desktop.w,
                                        {
                                            fm: 'webp' as const,
                                            q: 95,
                                        }
                                    )}
                                    sizes="(max-width: 768px) 100vw, 33vw"
                                    alt={
                                        panelData.image?.description ||
                                        panelData.image?.name
                                    }
                                    className={styles.image}
                                />
                            </div>
                        )}
                        <div
                            className={styles.headline}
                            data-kontent-element-codename="headline"
                        >
                            {panelData.headline}
                        </div>
                        <div
                            className={styles.subtitle}
                            data-kontent-element-codename="sub_title"
                        >
                            {panelData.sub_title}
                        </div>
                    </div>
                    <div
                        className={styles.description}
                        style={descriptionStyle}
                    >
                        <RichTextEl
                            richText={panelData.description}
                            elementCodename="description"
                        />
                    </div>
                    {panelData.cta_location && (
                        <div className={styles.ctaContainer}>
                            <CtaLink
                                href={panelData.cta_location}
                                target={ctaTarget}
                            >
                                {panelData.cta_label}
                            </CtaLink>
                        </div>
                    )}
                </div>
            );
        }
    };

    return (
        <section
            className={styles.wrapper}
            data-kontent-item-id={kontentItemId}
        >
            {breakpoints.l && renderArrowPrev()}
            <div className={styles.panelsContainer}>
                {visiblePanels.map((panel, idx) => renderPanel(panel, idx))}
            </div>
            {breakpoints.l && renderArrowNext()}
        </section>
    );
};

export default ColumnizedContent;

export const fragmentColumnizedContent = graphql`
    fragment ColumnizedContent on kontent_item_component___columnized_content {
        elements {
            line_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            content_panels {
                value {
                    ... on kontent_item_content_item___general_structured_panel {
                        id
                        elements {
                            sub_title {
                                value
                            }
                            headline {
                                value
                            }
                            description {
                                ...kontentRichText
                            }
                            image {
                                ...kontentImage
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    name
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            content_alignment {
                                value {
                                    name
                                    codename
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
